import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

// ── Benchmark config ──────────────────────────────────────────
export const BENCHMARKS = {
  USD: { options: ['SOFR', 'Fixed'],  label: 'SOFR'  },
  EUR: { options: ['€STR', 'Fixed'],  label: '€STR'  },
  GBP: { options: ['SONIA', 'Fixed'], label: 'SONIA' },
  SGD: { options: ['SORA', 'Fixed'],  label: 'SORA'  },
  JPY: { options: ['TONA', 'Fixed'],  label: 'TONA'  },
  AED: { options: ['Fixed'],          label: 'Fixed'  },
};

export const CURRENCIES     = ['USD', 'EUR', 'GBP', 'SGD', 'JPY', 'AED'];
export const LC_TYPES       = ['Sight', 'Usance'];
export const HEDGE_STATUSES = ['Unhedged', 'Partially Hedged', 'Fully Hedged'];

export const INDEX_BADGE_COLORS = {
  SOFR:  'bg-blue-900 text-blue-300',
  '€STR':'bg-purple-900 text-purple-300',
  SONIA: 'bg-teal-900 text-teal-300',
  SORA:  'bg-amber-900 text-amber-300',
  TONA:  'bg-gray-700 text-gray-300',
  Fixed: 'bg-gray-700 text-gray-400',
};

export function defaultIndexForCcy(ccy) {
  return BENCHMARKS[ccy]?.options[0] || 'Fixed';
}

// ── Day count convention ──────────────────────────────────────
export function dayCountDivisor(currency) {
  return currency === 'GBP' ? 365 : 360;
}

// ── Live FX rates hook ────────────────────────────────────────
export function useFxRates() {
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchRates() {
      const { data, error } = await supabase
        .from('fx_rates')
        .select('currency_pair, rate');
      if (!error && data) {
        const map = {};
        data.forEach(r => { map[r.currency_pair] = parseFloat(r.rate); });
        setRates(map);
      }
      setLoading(false);
    }
    fetchRates();
    const interval = setInterval(fetchRates, 30000);
    return () => clearInterval(interval);
  }, []);

  return { rates, loading };
}

// ── Interest rates hook ───────────────────────────────────────
export function useInterestRates() {
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchRates() {
      const { data, error } = await supabase
  .from('interest_rates')
  .select('rate_code, rate_value, currency, convention, overnight_spread, rate_name');
      if (!error && data) {
        const map = {};
       data.forEach(r => { 
  const policyRate = parseFloat(r.rate_value); 
  const spread = parseFloat(r.overnight_spread) || 0;
  map[r.rate_code] = { rate: policyRate, overnightRate: parseFloat((policyRate + spread).toFixed(4)), overnightSpread: spread, name: r.rate_name, convention: r.convention, currency: r.currency }; 
});
        setRates(map);
      }
      setLoading(false);
    }
    fetchRates();
  }, []);

  return { rates, loading };
}

// ── Rate code by currency ─────────────────────────────────────
const CURRENCY_RATE_CODE = {
  USD: 'FED_FUNDS',
  EUR: 'ECB_DEPOSIT',
  GBP: 'BOE_BASE',
  JPY: 'BOJ_POLICY',
  SGD: 'MAS_SORA',
  AED: 'CBUAE_BASE',
};

// ── Theoretical forward rate — Interest Rate Parity ───────────
// Forward = Spot × (1 + RBI × tenor/365) / (1 + Foreign × tenor/foreign_divisor)
export function calcTheoreticalForward(ccy, spotRate, tenorDays, interestRates) {
  if (!spotRate || !tenorDays || !interestRates) return spotRate || 0;

  const rbiRate     = interestRates['RBI_REPO']?.rate || 6.25;
  const rateCode    = CURRENCY_RATE_CODE[ccy];
  const foreignData = rateCode ? interestRates[rateCode] : null;
  const foreignRate = foreignData?.rate || 0;
  const foreignDiv  = foreignData?.convention === 'ACT/360' ? 360 : 365;

  const forward = spotRate
    * (1 + (rbiRate    / 100) * (tenorDays / 365))
    / (1 + (foreignRate / 100) * (tenorDays / foreignDiv));

  return parseFloat(forward.toFixed(4));
}

// ── Forward points ────────────────────────────────────────────
export function calcForwardPoints(ccy, spotRate, tenorDays, interestRates) {
  const fwd = calcTheoreticalForward(ccy, spotRate, tenorDays, interestRates);
  return parseFloat((fwd - spotRate).toFixed(4));
}

// ── Forward premium % p.a. ────────────────────────────────────
export function calcForwardPremium(ccy, spotRate, tenorDays, interestRates) {
  const fwd = calcTheoreticalForward(ccy, spotRate, tenorDays, interestRates);
  return parseFloat(((fwd - spotRate) / spotRate * (365 / tenorDays) * 100).toFixed(4));
}

// ── Resolve live spot ─────────────────────────────────────────
export function resolveSpot(ccy, fxRates, storedRate) {
  return fxRates[`${ccy.toUpperCase()}INR`] || storedRate || 0;
}

// ── Usance helpers ────────────────────────────────────────────
export function effectivePaymentDate(lc) {
  if (lc.lc_type !== 'Usance' || !lc.usance_period_days) return lc.maturity_date;
  const d = new Date(lc.maturity_date);
  d.setDate(d.getDate() + parseInt(lc.usance_period_days));
  return d.toISOString().split('T')[0];
}

export function lcTenorDays(lc) {
  const issue   = new Date(lc.issue_date);
  const effDate = new Date(effectivePaymentDate(lc));
  return Math.round((effDate - issue) / 86400000);
}

// ── LC Pricing ────────────────────────────────────────────────
export function totalLcPricingInr(lc, fxRates) {
  const liveSpot   = fxRates[`${lc.currency.toUpperCase()}INR`] || lc.spot_rate_inr || 0;
  const tenorDays  = lcTenorDays(lc);
  const divisor    = dayCountDivisor(lc.currency);
  const commission = lc.amount_fcy * liveSpot * ((lc.commission_rate || 0) / 100) * (tenorDays / divisor);
  return (
    commission +
    (lc.issuance_fee_inr    || 0) +
    (lc.swift_fee_inr       || 0) +
    (lc.amendment_fee_inr   || 0) +
    (lc.negotiation_fee_inr || 0) +
    (lc.other_fee_inr       || 0)
  );
}

// ── FX Risk ───────────────────────────────────────────────────
export function calcFxRiskInr(amtFcy, hedgedAmt, ccy, fxRates, storedRate, stressPct = 0.02) {
  const unhedged = amtFcy - (hedgedAmt || 0);
  if (unhedged <= 0) return 0;
  const spot = fxRates[`${ccy.toUpperCase()}INR`] || storedRate || 0;
  return unhedged * spot * stressPct;
}

// ── Estimated Cash Flow ───────────────────────────────────────
// Hedged portion   → contracted forward rate (locked with bank)
// Unhedged portion → theoretical forward rate (IRP-based)
export function calcEstimatedCfInr(lc, fxRates, interestRates) {
  const liveSpot    = fxRates[`${lc.currency.toUpperCase()}INR`] || lc.spot_rate_inr || 0;
  const tenorDays   = lcTenorDays(lc);
  const hedged      = lc.hedged_amount_fcy || 0;
  const unhedged    = lc.amount_fcy - hedged;

  // Hedged portion — use contracted forward rate locked with bank
  const hedgedCf    = hedged * (lc.contracted_rate_inr || liveSpot);

  // Unhedged portion — use theoretical forward rate from IRP
  const theorFwd    = calcTheoreticalForward(lc.currency, liveSpot, tenorDays, interestRates);
  const unhedgedCf  = unhedged * theorFwd;

  return hedgedCf + unhedgedCf;
}

// ── BC Cash Flow ──────────────────────────────────────────────
export function calcBcCfInr(bc, fxRates, interestRates) {
  const liveSpot  = fxRates[`${bc.currency.toUpperCase()}INR`] || bc.spot_rate_inr || 0;
  const tenor     = bc.tenor_days || 90;
  const hedged    = bc.hedged_amount_fcy || 0;
  const unhedged  = bc.amount_fcy - hedged;

  const hedgedCf  = hedged * (bc.contracted_rate_inr || liveSpot);
  const theorFwd  = calcTheoreticalForward(bc.currency, liveSpot, tenor, interestRates);
  const unhedgedCf = unhedged * theorFwd;

  return hedgedCf + unhedgedCf;
}

// ── BC Interest Cost ──────────────────────────────────────────
export function bcInterestInr(amtFcy, ccy, fxRates, storedRate, allInRate, tenorDays) {
  const liveSpot = fxRates[`${ccy.toUpperCase()}INR`] || storedRate || 0;
  const divisor  = dayCountDivisor(ccy);
  return amtFcy * liveSpot * ((allInRate || 0) / 100) * (tenorDays / divisor);
}

// ── Formatters ────────────────────────────────────────────────
export function daysToDate(dateStr) {
  if (!dateStr) return 0;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

export function fmtCr(n) {
  if (!n || isNaN(n)) return '₹0.00 Cr';
  return '₹' + (n / 10000000).toFixed(2) + ' Cr';
}

export function fmtL(n) {
  if (!n || isNaN(n)) return '₹0.0L';
  if (n >= 10000000) return fmtCr(n);
  return '₹' + (n / 100000).toFixed(1) + 'L';
}

export function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtDateShort(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// ── Hedge-aware CF calculation ────────────────────────────────
// Fetches linked hedges from hedge_positions and calculates CF
// Priority: actual hedges → manual fields → IRP forward

export function calcCfFromHedges(record, hedges, fxRates, interestRates, type = 'lc') {
  const ccy      = record.currency;
  const liveSpot = fxRates[`${ccy.toUpperCase()}INR`] || 0;
  const amtFcy   = record.amount_fcy || 0;

  // Filter hedges linked to this record
  const linked = (hedges || []).filter(h =>
    h.underlying_type === (type === 'lc' ? 'LC' : 'BC') &&
    h.underlying_ref  === (type === 'lc' ? record.lc_ref : record.bc_ref) &&
    !['Settled','Cancelled'].includes(h.status)
  );

  if (linked.length === 0) {
    // No linked hedges — fall back to IRP forward
    return calcEstimatedCfInr(record, fxRates, interestRates);
  }

  // Total hedged amount and weighted avg rate from actual hedges
  const totalHedged = linked.reduce((s, h) => s + (parseFloat(h.notional_amount_num) || 0), 0);
  const wtdRate     = linked.reduce((s, h) => {
    const n = parseFloat(h.notional_amount_num) || 0;
    const r = parseFloat(h.rate_locked_num) || liveSpot;
    return s + n * r;
  }, 0) / (totalHedged || 1);

  const hedgedAmt   = Math.min(totalHedged, amtFcy);
  const unhedgedAmt = Math.max(0, amtFcy - hedgedAmt);

  // Hedged portion at contracted rate, unhedged at IRP forward
  const tenor = type === 'lc'
    ? (() => { const e = effectivePaymentDate(record); return Math.max(1, Math.round((new Date(e) - new Date()) / 86400000)); })()
    : (record.tenor_days || 90);

  const theorFwd    = calcTheoreticalForward(ccy, liveSpot, tenor, interestRates);
  const hedgedCf    = hedgedAmt   * wtdRate;
  const unhedgedCf  = unhedgedAmt * theorFwd;

  return hedgedCf + unhedgedCf;
}

export function deriveHedgeStatus(record, hedges, type = 'lc') {
  const amtFcy  = record.amount_fcy || 0;
  const linked  = (hedges || []).filter(h =>
    h.underlying_type === (type === 'lc' ? 'LC' : 'BC') &&
    h.underlying_ref  === (type === 'lc' ? record.lc_ref : record.bc_ref) &&
    !['Settled','Cancelled'].includes(h.status)
  );
  if (!linked.length) return record.hedge_status || 'Unhedged';
  const totalHedged = linked.reduce((s, h) => s + (parseFloat(h.notional_amount_num) || 0), 0);
  const ratio = amtFcy > 0 ? totalHedged / amtFcy : 0;
  if (ratio >= 0.99)  return 'Fully Hedged';
  if (ratio > 0)      return 'Partially Hedged';
  return 'Unhedged';
}

export function deriveHedgedAmount(record, hedges, type = 'lc') {
  const linked = (hedges || []).filter(h =>
    h.underlying_type === (type === 'lc' ? 'LC' : 'BC') &&
    h.underlying_ref  === (type === 'lc' ? record.lc_ref : record.bc_ref) &&
    !['Settled','Cancelled'].includes(h.status)
  );
  return Math.min(
    linked.reduce((s, h) => s + (parseFloat(h.notional_amount_num) || 0), 0),
    record.amount_fcy || 0
  );
}
